import { useAuth } from '@clerk/react'
import { createContext, use, useMemo, useState } from 'react'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useOpenSettingsSection } from '@/hooks/use-open-settings-section'
import {
  EditorSettingsModal,
  type EditorSettingsSlug,
} from './editor-settings-modal'

export interface OpenEditorSettingsOptions {
  /**
   * Where focus returns when the modal closes. Defaults to whatever is focused
   * when it opens; pass a stable control when that one is about to unmount
   * (e.g. a button inside a popover that closes as the modal opens).
   */
  returnFocusTo?: HTMLElement | null
}

/** Opens the modal on a section; null while the modal is unavailable. */
type OpenModal = (
  slug: EditorSettingsSlug,
  options?: OpenEditorSettingsOptions,
) => void

const EditorSettingsModalContext = createContext<OpenModal | null>(null)

/**
 * Owns the editor settings modal. It is for signed-in users behind
 * `enable-settings-page`, like the `/settings` page it previews: signed out,
 * or with the flag off (or still loading), it provides nothing, so every entry
 * point falls back to Clerk's profile modal and nothing new renders.
 */
export function EditorSettingsModalProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { isSignedIn } = useAuth()
  const { enabled: flagEnabled } = useFeatureFlag('enable-settings-page')
  const enabled = flagEnabled && isSignedIn === true
  const [slug, setSlug] = useState<EditorSettingsSlug | null>(null)
  const [returnFocusTo, setReturnFocusTo] = useState<HTMLElement | null>(null)
  // Losing availability (e.g. signing out) drops the open section, so the
  // modal cannot pop back open by itself when availability returns.
  if (!enabled && slug !== null) setSlug(null)

  const open = useMemo<OpenModal | null>(
    () =>
      enabled
        ? (next, options) => {
            setReturnFocusTo(
              options?.returnFocusTo ??
                (document.activeElement instanceof HTMLElement
                  ? document.activeElement
                  : null),
            )
            setSlug(next)
          }
        : null,
    [enabled],
  )

  return (
    <EditorSettingsModalContext value={open}>
      {children}
      {open && (
        <EditorSettingsModal
          slug={slug}
          onSelect={setSlug}
          onClose={() => setSlug(null)}
          returnFocusTo={returnFocusTo}
        />
      )}
    </EditorSettingsModalContext>
  )
}

/**
 * The editor's way into its settings. `open` always goes somewhere: the
 * section in the editor settings modal when `hasModal`, otherwise the settings
 * deep-link seam, which opens Clerk's profile modal tab while
 * `enable-settings-page` is off.
 */
export function useEditorSettings(): {
  open: (slug: EditorSettingsSlug, options?: OpenEditorSettingsOptions) => void
  /** Whether `open` shows the modal (signed in, flag on); gate modal-only UI on it. */
  hasModal: boolean
} {
  const openModal = use(EditorSettingsModalContext)
  const openSettingsSection = useOpenSettingsSection()
  return {
    open: (slug, options) => {
      if (openModal) {
        openModal(slug, options)
      } else {
        openSettingsSection(slug, { newTab: true })
      }
    },
    hasModal: openModal !== null,
  }
}
